// Page Aujourd'hui : la liste du jour et son tableau de bord. À gauche ce qu'on fait — le retard à part, puis les
// séances du jour rangées par spécialité sous un en-tête teinté par la famille de la matière. À droite, fixe, ce
// qu'on surveille : « Ta journée » (barre de temps, faites, reste, Répartir), les partiels qu'aucun groupe ne porte
// en entier, les podcasts. Sur téléphone la colonne devient une bande de résumé en haut, puis la liste ; les podcasts
// passent en bas. Chaque séance n'a qu'une action visible, « Ouvrir » ; la plus urgente la porte en bouton plein.
import { html, attr } from '../../core/html.js';
import { state } from '../../core/store.js';
import { ico } from '../icones.js';
import { renderSeance } from '../components/taskRow.js';
import { bandeauPartiels, bandeauDecoupages, bandeauPodcastsCompact } from '../components/bandeaux.js';

function dateLongue(ymd) {
    var d = window.parseDateLocal(ymd), s = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    return s.charAt(0).toUpperCase() + s.slice(1);
}
function pluriel(n, un, des) { return n + ' ' + (n > 1 ? des : un); }
function faite(t) { return t.status === 'done'; }
function aFaire(t) { return t.status === 'pending'; }
function classeMat(matiere) { var f = window.familleMatiere(matiere); return f ? ' mat-' + f : ''; }
function nomMatiere(t) { return t.matiere || 'Sans matière'; }
// Les minutes d'une séance dans la barre : l'estimation, faite ou non, pour qu'un segment ne change pas de largeur
// quand on le coche.
function minutes(t) { return Math.max(1, window.estimateTaskMinutes(t).total || 0); }
function duree(min) { return min ? '≈ ' + window.formatDuration(min) : ''; }
function restant(taches) { return window.estimateDayMinutes(taches).total; }

// Le bouton reste visible même sans retard : on sait ainsi qu'il existe avant d'en avoir besoin.
function aideRepartir(n, nJour) {
    var quoi = n ? pluriel(n, 'séance en retard', 'séances en retard')
        : (nJour ? pluriel(nJour, "séance d'aujourd'hui", "séances d'aujourd'hui") : 'rien pour le moment');
    if (!n && !nJour) return "Rien à décaler : aucune séance en retard, et la journée est vide.";
    return (n ? 'Replace les ' : "Aucun retard : pousse les ") + quoi
        + " sur les jours qui viennent, un jour sur deux à partir du surlendemain. La suite de chaque séquence suit du même décalage ; les séances cochées ne bougent pas.";
}

// Deux ordres de document, un par mise en page, pour que le clavier et le lecteur d'écran suivent toujours l'écran
// (WCAG 2.4.3 et 1.3.2) : à partir de 1024 px, la liste puis la colonne collante (« Ta journée », puis le suivi),
// comme la maquette D ; en dessous, la bande « Ta journée », la liste, puis le suivi (partiels, découpages, podcasts).
// Le CSS ne réordonne rien (aucun `order`) : c'est le document qui porte l'ordre. Le seuil est celui de
// src/styles/pages/aujourdhui.css ; le franchir (rotation d'une tablette, fenêtre qu'on élargit) redessine la page.
var LARGE = '(min-width: 1024px)';
function large() { try { return window.matchMedia(LARGE).matches; } catch (e) { return true; } }
try {
    var seuil = window.matchMedia(LARGE), redessiner = function() {
        if (state.activeTab === 'dashboard' && !state.isLoading && window.renderContent) window.renderContent();
    };
    if (seuil.addEventListener) seuil.addEventListener('change', redessiner); else if (seuil.addListener) seuil.addListener(redessiner);
} catch (e) {}

export function squelettePage() {
    return html`<div class="squelettes" aria-hidden="true">
        <div class="jour-entete"><div><div class="squelette squelette-titre" style="width:260px"></div><div class="squelette squelette-ligne" style="width:200px"></div></div><div class="squelette squelette-ligne" style="width:180px;height:10px"></div></div>
        ${[1, 2, 3, 4].map(function() { return html`<div class="seance seance--squelette"><div class="squelette squelette-coche"></div><div class="seance-corps"><div class="squelette squelette-ligne" style="width:60%"></div><div class="squelette squelette-ligne" style="width:35%"></div></div></div>`; })}
    </div>`;
}

// Les groupes de spécialité, dans l'ordre de leur première séance : on groupe une liste déjà triée
// (sortTasksForExams), sans la retrier.
function grouper(taches) {
    var parMatiere = {}, groupes = [];
    taches.forEach(function(t) {
        var m = nomMatiere(t);
        if (!parMatiere[m]) { parMatiere[m] = { matiere: m, taches: [] }; groupes.push(parMatiere[m]); }
        parMatiere[m].taches.push(t);
    });
    return groupes;
}

// Le retard du matin. Une séance en retard qu'on coche quitte la liste (elle n'est plus à faire, et elle n'est pas du
// jour) ; la barre la garde pourtant, remplie, pour que « En retard 1/6 » dise ce qui a été rattrapé. Le moteur ne date
// pas un cochage : on retient donc ici, pour la journée, les séances vues en retard depuis l'ouverture de la page. Un
// rechargement repart de ce qui reste à faire ; une séance replacée plus tard (Répartir, Pas acquis, Déplacer) n'est
// plus en retard et sort d'elle-même.
var matin = { jour: null, ids: [] };
function retardDuJour(td, enRetard) {
    if (matin.jour !== td) matin = { jour: td, ids: [] };
    enRetard.forEach(function(t) { if (matin.ids.indexOf(t.id) === -1) matin.ids.push(t.id); });
    var parId = {};
    state.tasks.forEach(function(t) { parId[t.id] = t; });
    // Les rattrapées d'abord, dans l'ordre où on les a vues : on coche du haut de la liste, le segment reste en place.
    var rattrapees = matin.ids.map(function(i) { return parId[i]; }).filter(function(t) { return t && t.date < td && faite(t); });
    return rattrapees.concat(enRetard);
}

function enteteSection(o) {
    var estim = restant(o.taches), e = o.partiel;
    return html`<header class="jour-section-entete">
        <h2 id="${o.id}-titre">${o.titre}</h2>
        <span class="jour-section-compte num">${pluriel(o.taches.length, 'séance', 'séances')}${estim ? html` · ${duree(estim)}` : ''}${e ? html` · <span title="${(e.name || 'Partiel') + ' le ' + window.formatDateFr(e.date)}">partiel J-${window.daysUntil(e.date)}</span>` : ''}</span>
        ${o.aide ? html`<p class="jour-section-aide">${o.aide}</p>` : ''}
    </header>`;
}

// Le partiel accroché à l'en-tête de sa matière : son nom (qui ouvre la gestion des partiels), J-n, et combien
// d'items de cette matière sont déjà vus — une matière, puisque l'en-tête en est une.
function partielDuGroupe(e, matiere) {
    var n = window.daysUntil(e.date), pg = window.progressForSpec(matiere), nom = e.name || 'Partiel';
    return html`<p class="jour-groupe-partiel">
        <span class="jour-partiel-nom">${ico('graduation-cap', { taille: 16 })}<button type="button" class="lien-bouton jour-partiel-lien" data-action="openExams" aria-label="${nom} : gérer les partiels" title="${nom + ' le ' + window.formatDateFr(e.date)}">Partiel</button></span>
        <span class="jour-partiel-j num${n <= 7 ? ' jour-partiel-j--proche' : ''}" title="${'Le ' + window.formatDateFr(e.date)}">${n === 0 ? 'aujourd\'hui' : 'J-' + n}</span>
        ${pg.total ? html`<span class="jour-partiel-avance" title="${matiere + ' : ' + pluriel(pg.done, 'item vu', 'items vus') + ' sur ' + pg.total + ' (tous leurs J0 cochés)'}">
            <span class="jauge-piste" role="progressbar" aria-valuemin="0" aria-valuemax="${pg.total}" aria-valuenow="${pg.done}" aria-label="Items de ${matiere} vus avant le partiel"><span class="jauge-val" style="--p:${(pg.pct / 100).toFixed(3)}"></span></span>
            <span class="num">${pg.done}/${pg.total}</span>
        </span>` : ''}
    </p>`;
}

function groupe(g, i, o) {
    var e = o.partielDe(g.matiere), estim = restant(g.taches);
    return html`<section class="jour-groupe${classeMat(g.matiere)}" aria-labelledby="groupe-${i}-titre">
        <header class="jour-groupe-tete">
            <h3 class="jour-groupe-nom" id="groupe-${i}-titre">${g.matiere}</h3>
            <span class="jour-groupe-compte num">${pluriel(g.taches.length, 'séance', 'séances')}${estim ? html` · ${duree(estim)}` : ''}</span>
            ${e ? partielDuGroupe(e, g.matiere) : ''}
        </header>
        <div class="jour-groupe-liste">${g.taches.map(function(t) {
            return renderSeance(t, { ouvrir: true, groupe: true, partielGroupe: e, primaire: t === o.premiere });
        })}</div>
    </section>`;
}

// --- « Ta journée » : la barre de temps et ses compteurs
// La barre récite ce qu'elle dessine : par zone, chaque suite de séances d'une même matière, avec ses minutes.
function reciter(segs) {
    var runs = [];
    segs.forEach(function(s) {
        var dernier = runs[runs.length - 1], mot = s.min + ' min' + (s.faite ? ' faite' : '');
        if (dernier && dernier.matiere === s.matiere) dernier.mots.push(mot); else runs.push({ matiere: s.matiere, mots: [mot] });
    });
    return runs.map(function(r) {
        var m = r.mots;
        return r.matiere + ' ' + (m.length > 1 ? m.slice(0, -1).join(', ') + ' et ' + m[m.length - 1] : m[0]);
    }).join(', ');
}
function zone(segs, retard) {
    var total = segs.reduce(function(s, x) { return s + x.min; }, 0);
    return html`<div class="jour-zone${retard ? ' jour-zone--retard' : ''}" style="--min:${total}">${segs.map(function(s) {
        return html`<i class="jour-seg${classeMat(s.matiere)}${s.faite ? ' jour-seg--faite' : ''}${s.nouveauGroupe ? ' jour-seg--groupe' : ''}" style="--min:${s.min}"></i>`;
    })}</div>`;
}
function segment(t, nouveauGroupe) { return { matiere: nomMatiere(t), min: minutes(t), faite: faite(t), nouveauGroupe: !!nouveauGroupe }; }

function journee(o) {
    var segR = o.retardVu.map(function(t) { return segment(t); });
    // Le jour rangé comme ses groupes : les faites d'abord (on coche du haut), puis ce qui reste, dans l'ordre de la liste.
    var segJ = [];
    o.groupesJour.forEach(function(g, gi) {
        g.taches.filter(faite).concat(g.taches.filter(aFaire)).forEach(function(t, k) { segJ.push(segment(t, gi > 0 && k === 0)); });
    });
    var resteR = restant(o.retard), resteJ = restant(o.aFaire), reste = resteR + resteJ;
    var faitesR = o.retardVu.filter(faite).length;
    var totalR = segR.reduce(function(s, x) { return s + x.min; }, 0), totalJ = segJ.reduce(function(s, x) { return s + x.min; }, 0);
    var libelle = 'Ta journée en minutes.'
        + (segR.length ? ' En retard, ' + pluriel(segR.length, 'séance', 'séances') + (faitesR ? ', dont ' + pluriel(faitesR, 'faite', 'faites') : '') + (resteR ? ', environ ' + window.formatDuration(resteR) + ' restantes' : '') + ' : ' + reciter(segR) + '.' : '')
        + (segJ.length ? ' Aujourd\'hui, ' + pluriel(segJ.length, 'séance', 'séances') + (o.faites.length ? ', dont ' + pluriel(o.faites.length, 'faite', 'faites') : '') + (resteJ ? ', environ ' + window.formatDuration(resteJ) + ' restantes' : '') + ' : ' + reciter(segJ) + '.' : '');
    // Légende : une pastille par famille présente, nommée par ses matières du jour — la couleur ne parle jamais seule.
    var familles = [], parFamille = {};
    segR.concat(segJ).forEach(function(s) {
        var cle = window.familleMatiere(s.matiere) || '-';
        if (!parFamille[cle]) { parFamille[cle] = { classe: classeMat(s.matiere), matieres: [] }; familles.push(parFamille[cle]); }
        if (parFamille[cle].matieres.indexOf(s.matiere) === -1) parFamille[cle].matieres.push(s.matiere);
    });
    var vide = !segR.length && !segJ.length;
    return html`<section class="carte jour-carte jour-journee" aria-labelledby="journee-titre">
        <h2 class="jour-carte-titre" id="journee-titre">Ta journée</h2>
        ${vide ? html`<p class="jour-carte-detail">Aucune séance à faire aujourd'hui.</p>` : html`<div class="jour-budget">
            <div class="jour-barre" role="img" aria-label="${libelle}">
                ${segR.length ? zone(segR, true) : ''}
                ${segJ.length ? zone(segJ, false) : ''}
            </div>
            <div class="jour-reperes num">
                ${segR.length ? html`<p class="jour-repere jour-repere--retard" style="--min:${totalR}"><b>En retard</b><span>${faitesR}/${segR.length} · ${resteR ? duree(resteR) : 'rattrapé'}</span></p>` : ''}
                ${segJ.length ? html`<p class="jour-repere" style="--min:${totalJ}"><b>Aujourd'hui</b><span>${segJ.length} · ${resteJ ? duree(resteJ) : 'fini'}</span></p>` : ''}
            </div>
        </div>`}
        <p class="jour-chiffres num" aria-live="polite">
            ${o.duJour.length ? html`<span class="jour-chiffre jour-faites"><strong>${o.faites.length}/${o.duJour.length}</strong>faites</span>` : ''}
            ${reste ? html`<span class="jour-chiffre jour-reste"><strong>${duree(reste)}</strong>restantes</span>` : ''}
            ${o.streak > 0 ? html`<span class="jour-chiffre jour-serie">${ico('flame', { taille: 16, classe: 'jour-flamme' })}<strong>${o.streak} j</strong>de série</span>` : ''}
        </p>
        ${familles.length ? html`<ul class="jour-legende" aria-label="Couleurs de la barre">${familles.map(function(f) {
            return html`<li class="${f.classe.trim()}"><i class="jour-pastille" aria-hidden="true"></i>${f.matieres.join(', ')}</li>`;
        })}</ul>` : ''}
        <button type="button" class="btn btn-secondaire btn-pleine jour-repartir" data-action="repartirRetards"${!o.retard.length && !o.aFaire.length ? html` disabled` : ''} title="${aideRepartir(o.retard.length, o.aFaire.length)}">${ico('calendar-dots', { taille: 18 })}${o.retard.length ? 'Répartir' : 'Décaler'} un jour sur deux${o.retard.length ? html` <span class="num jour-repartir-n">(${o.retard.length})</span>` : ''}</button>
    </section>`;
}

export function renderAujourdhui() {
    var td = window.todayStr();
    if (state.serverStatus === 'offline' && state.tasks.length === 0) return window.renderServeurInjoignable();
    if (state.tasks.length === 0) {
        return html`<div class="vide carte">${ico('calendar-dots')}<h2>Aucune séance planifiée</h2><p>Choisis tes matières et lance l'ordonnancement : chaque item reviendra au bon moment.</p><button type="button" class="btn btn-primaire" data-action="ouvrirAutoplan">${ico('magic-wand', { taille: 18 })}Planifier</button></div>`;
    }
    var duJour = state.tasks.filter(function(t) { return t.date === td; });
    var restantJour = duJour.filter(aFaire), faites = duJour.filter(faite);
    var retard = window.sortTasksForExams(state.tasks.filter(function(t) { return t.date < td && aFaire(t); }));
    var retardVu = retardDuJour(td, retard);
    // L'ordre des groupes est celui de leur première séance dans la journée triée, faites comprises : un groupe ne
    // saute pas quand on coche sa première séance, et la barre de temps suit le même ordre que la liste.
    var groupesJour = grouper(window.sortTasksForExams(duJour));
    var groupesAFaire = groupesJour.map(function(g) { return { matiere: g.matiere, taches: g.taches.filter(aFaire) }; })
        .filter(function(g) { return g.taches.length; });
    var premiere = retard[0] || (groupesAFaire[0] ? groupesAFaire[0].taches[0] : null);
    // Quand toutes les séances en retard mènent au même partiel, il se dit une fois, dans le compte de la section,
    // plutôt que sur chaque ligne ; sinon chaque ligne garde le sien.
    var partielRetard = retard.length ? window.examForTask(retard[0]) : null;
    if (partielRetard && !retard.every(function(t) { return window.examForTask(t) === partielRetard; })) partielRetard = null;
    // Un partiel s'accroche à l'en-tête de sa matière (le plus proche, s'il y en a deux). L'en-tête ne dit que cette
    // matière : un partiel à plusieurs matières reste donc AUSSI en résumé dans la colonne, avec son nom, ses items du
    // jour et la jauge de chacune de ses matières — celles qu'aucun groupe du jour ne porte n'ont que ce résumé. Un
    // partiel qu'aucun groupe ne porte n'est que dans la colonne.
    var exams = window.activeExams(td);
    var partielDe = function(m) { return exams.filter(function(e) { return (e.specs || []).indexOf(m) !== -1; })[0] || null; };
    var libres = exams.filter(function(e) {
        return (e.specs || []).length > 1 || !groupesAFaire.some(function(g) { return partielDe(g.matiere) === e; });
    });
    var aFaireListe = retard.concat(groupesAFaire.reduce(function(l, g) { return l.concat(g.taches); }, []));
    var o = { duJour: duJour, aFaire: restantJour, faites: faites, retard: retard, retardVu: retardVu, groupesJour: groupesJour, streak: window.computeStreak() };
    // Un seul bouton plein à l'écran : « Ouvrir » tant qu'une séance reste à faire (retard compris), puis « QCM du
    // soir » une fois tout fait. « Appliquer » (découpages en lot) ne le prend que si aucun des deux n'est là.
    var soir = bilanDuSoir(!!premiere);
    var suivi = [libres.length ? bandeauPartiels(td, libres) : '', bandeauDecoupages({ secondaire: !!premiere || !!String(soir) }), bandeauPodcastsCompact(aFaireListe)].filter(function(x) { return String(x); });
    var blocSuivi = suivi.length ? html`<div class="jour-suivi">${suivi}</div>` : '';
    var entete = html`<header class="jour-entete">
            <h1 class="jour-date">${dateLongue(td)}</h1>
        </header>`;
    var listes = html`<div class="jour-listes">
            ${retard.length ? html`<section class="jour-section jour-section--retard" aria-labelledby="retard-titre">
                ${enteteSection({ id: 'retard', titre: 'En retard', taches: retard, partiel: partielRetard,
                    aide: 'Prévues avant aujourd\'hui et pas encore faites. Coche-les, déplace-les depuis leur menu, ou répartis-les sur les jours qui viennent.' })}
                <div class="jour-registre">${retard.map(function(t) { return renderSeance(t, { ouvrir: true, retard: true, partielGroupe: partielRetard, primaire: t === premiere }); })}</div>
            </section>` : ''}
            ${restantJour.length ? html`<section class="jour-section" aria-labelledby="aujourdhui-titre">
                ${enteteSection({ id: 'aujourdhui', titre: 'Aujourd\'hui', taches: restantJour })}
                <div class="jour-registre">${groupesAFaire.map(function(g, i) { return groupe(g, i, { partielDe: partielDe, premiere: premiere }); })}</div>
            </section>` : (duJour.length ? html`<div class="vide">${ico('check-circle')}<h2>Tout est fait pour aujourd'hui</h2><p>${pluriel(faites.length, 'séance cochée', 'séances cochées')}. À demain.</p></div>` : html`<div class="vide">${ico('sun')}<h2>Rien de prévu aujourd'hui</h2><p>Le planning reprend au prochain jour planifié.</p></div>`)}
            ${faites.length ? html`<section class="jour-section jour-section--faites" aria-labelledby="faites-titre">
                ${enteteSection({ id: 'faites', titre: 'Faites aujourd\'hui', taches: faites })}
                <div class="jour-registre">${faites.map(function(t) { return renderSeance(t, { ouvrir: true }); })}</div>
            </section>` : ''}
            ${soir}
        </div>`;
    if (large()) return html`<div class="jour fade-in">
        ${entete}
        ${listes}
        <aside class="jour-cote" aria-label="Ta journée">
            ${journee(o)}
            ${blocSuivi}
        </aside>
    </div>`;
    return html`<div class="jour fade-in">
        ${entete}
        ${journee(o)}
        ${listes}
        ${blocSuivi}
    </div>`;
}

// Le bilan du soir : reprendre d'un trait, en QCM ou en flashcards, tout ce qui a été coché dans la journée.
// Il n'apparaît que s'il y a quelque chose à reprendre, et il ne touche jamais au planning. Il paraît dès la première
// séance du jour cochée : tant qu'il reste une séance à faire (retard compris), « QCM du soir » est en secondaire et
// le seul bouton plein reste « Ouvrir » ; il ne passe en plein qu'une fois tout fait.
function bilanDuSoir(resteAFaire) {
    var items = window.itemsDuJour(state.tasks, window.todayStr(), state.subParts);
    if (!items.length) return '';
    var noms = items.slice(0, 3).map(function(e) { return e.n; }).join(', ');
    return html`<section class="jour-soir" aria-labelledby="soir-titre">
        <div class="jour-soir-texte">
            <h2 id="soir-titre">${ico('moon', { taille: 18 })}Bilan du soir</h2>
            <p>Reprends d'un trait ${items.length > 1 ? 'les ' + items.length + ' items travaillés aujourd\'hui' : 'l\'item travaillé aujourd\'hui'}
                <span class="num">(${noms}${items.length > 3 ? '…' : ''})</span>. Les items s'enchaînent, chacun annoncé par son nom, avec le compte général en haut de l'écran.</p>
        </div>
        <div class="jour-soir-actions">
            <button type="button" class="btn ${resteAFaire ? 'btn-secondaire' : 'btn-primaire'} btn-sm" data-action="soirDemarrer" data-args="${attr(['qcm'])}">${ico('question', { taille: 16 })}QCM du soir</button>
            <button type="button" class="btn btn-secondaire btn-sm" data-action="soirDemarrer" data-args="${attr(['flash'])}">${ico('cards-three', { taille: 16 })}Flashcards du soir</button>
        </div>
    </section>`;
}

window.renderAujourdhui = renderAujourdhui;
window.squelettePage = squelettePage;
